import { money, allocateDiscount, toPriceObject } from '~/utils/money';

/**
 * Get the selectedOptions from a Shopify variant by its numeric ID.
 */
const getSelectedOptions = (product, variantId) => {
  const edge = product.shopifyProduct?.variants?.edges.find((v) => v.node.id.endsWith('/' + String(variantId)));
  return edge?.node.selectedOptions || [];
};

/**
 * Size groups (equivalent sizes) and ordered size scale for fallback matching.
 * Groups: XS≈S, M≈L, XL≈XXL
 */
const SIZE_GROUPS = [
  ['XS', 'S'],
  ['M', 'L'],
  ['XL', 'XXL'],
];
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const isDimension = (v) => /^\d+\s*[x×]\s*\d+$/i.test(String(v).trim());

/**
 * Compare two option values with weighted matching.
 * Returns: 4 = exact, 3 = same size group, 2 = 1 step away, 1 = 2 steps away, 0 = no match.
 * e.g. "M" ↔ "M (40,5 - 42)" = 4, "M" ↔ "L" = 3, "XL" ↔ "L" = 2
 * Dimension values (e.g. "80x200") only match exactly.
 */
const optionValuesMatch = (a, b) => {
  if (a === b) return 4;
  if (isDimension(a) || isDimension(b)) return 0; // exact match only for dimensions

  const normalize = (v) => v.split(/[\s(]/)[0];
  const tokenA = normalize(a);
  const tokenB = normalize(b);
  if (tokenA === tokenB) return 4;

  // Same size group
  const groupA = SIZE_GROUPS.findIndex((g) => g.includes(tokenA));
  const groupB = SIZE_GROUPS.findIndex((g) => g.includes(tokenB));
  if (groupA !== -1 && groupA === groupB) return 3;

  // Ordered distance fallback (nearest size)
  const indexA = SIZE_ORDER.indexOf(tokenA);
  const indexB = SIZE_ORDER.indexOf(tokenB);
  if (indexA === -1 || indexB === -1) return 0;
  const distance = Math.abs(indexA - indexB);
  if (distance <= 1) return 2;
  if (distance <= 2) return 1;
  return 0;
};

const findMatchingVariantByOptions = (targetProduct, sourceOptions) => {
  if (!sourceOptions.length || !targetProduct.shopifyProduct?.variants?.edges.length) return null;

  let bestVariant = null;
  let bestScore = 0;
  let bestAvailable = false;

  for (const edge of targetProduct.shopifyProduct.variants.edges) {
    const candidateOptions = edge.node.selectedOptions || [];
    let score = 0;

    for (const source of sourceOptions) {
      const candidate = candidateOptions.find((opt) => opt.name === source.name);
      if (candidate) score += optionValuesMatch(candidate.value, source.value);
    }

    if (score === 0) continue;

    const candidateId = parseInt(edge.node.id.split('/').pop(), 10);
    const pickystoryVariant = targetProduct.variants.find((v) => v.id === candidateId);
    const available = pickystoryVariant?.available !== false;

    if (score > bestScore || (score === bestScore && available && !bestAvailable)) {
      bestVariant = pickystoryVariant;
      bestScore = score;
      bestAvailable = available;
    }
  }

  return bestVariant;
};

/**
 * Composable for managing PickyStory deals with reactive pricing
 * Handles both BND (combos) and FBT (bundles) deal types
 * Uses Dinero.js for accurate money calculations
 *
 * @param {Ref<Object|null>} dealRef - Reactive reference to the deal object
 * @param {Object} options - Configuration options
 * @param {Ref<Array>} options.cartLineItems - Cart line items for CartDeals variant detection
 * @returns {Object} Reactive deal state and methods
 */
export const usePickystoryDeal = (dealRef, options = {}) => {
  const { cartLineItems = ref([]) } = options;

  // Tracks selected variant IDs for each product
  const selectedVariants = ref({});

  /**
   * Initialize selected variants when deal loads
   * For CartDeals: finds variants already in cart
   * For CrossSell: finds first available variant
   */
  const initializeVariants = (deal, lineItems = []) => {
    const selected = {};

    if (!deal) return selected;

    const products = deal.isBogo ? [...deal.products, ...deal.discountProducts] : deal.products;

    const cartMatchedProductIds = new Set();

    products.forEach((product) => {
      if (lineItems.length > 0) {
        // CartDeals mode: find which variant is already in cart
        const variantInCart = product.variants.find((v) => lineItems.some((item) => item.variant.sku === v.sku));
        selected[product.id] = variantInCart?.id || product.variants[0]?.id;
        if (variantInCart) cartMatchedProductIds.add(product.id);
      } else {
        // CrossSell mode: find first available variant
        const availableVariant = product.variants.find((v) => v.available);
        selected[product.id] = availableVariant?.id || product.variants[0]?.id;
      }
    });

    if (cartMatchedProductIds.size > 0) {
      const matchedProducts = [];
      const unmatchedProducts = [];
      for (const p of products) {
        (cartMatchedProductIds.has(p.id) ? matchedProducts : unmatchedProducts).push(p);
      }

      if (unmatchedProducts.length > 0) {
        for (const matchedProduct of matchedProducts) {
          const sourceOptions = getSelectedOptions(matchedProduct, selected[matchedProduct.id]);
          if (!sourceOptions.length) continue;

          for (const targetProduct of unmatchedProducts) {
            const match = findMatchingVariantByOptions(targetProduct, sourceOptions);
            if (match) selected[targetProduct.id] = match.id;
          }
        }
      }
    }

    return selected;
  };

  // Watch for deal changes and initialize variants
  watch(
    dealRef,
    (newDeal) => {
      if (newDeal) {
        selectedVariants.value = initializeVariants(newDeal, cartLineItems.value);
      }
    },
    { immediate: true }
  );

  /**
   * Computed productsList - maintains same structure as before
   * Each product has a 'selected' property pointing to the full variant object
   * Now includes exactDiscountedPrice calculated via proportional allocation
   */
  const productsList = computed(() => {
    if (!dealRef.value) return [];

    const products = dealRef.value.isBogo
      ? [...dealRef.value.products, ...dealRef.value.discountProducts]
      : dealRef.value.products;

    // Calculate exact discounted prices using proportional allocation
    const productsWithPrices = products.map((product) => {
      const selectedId = selectedVariants.value[product.id];
      const selected = product.variants.find((v) => v.id === selectedId);

      return {
        ...product,
        selected: selected || product.variants[0],
        priceForAllocation: (selected || product.variants[0]).price.amount,
      };
    });

    // Only allocate if there's a discount
    let allocatedPrices = null;
    if (
      dealRef.value.discountValue &&
      dealRef.value.discountValue > 0 &&
      bundleCompareAtPrice.value &&
      bundlePrice.value
    ) {
      const totalDiscount = bundleCompareAtPrice.value.amount - bundlePrice.value.amount;
      const currency = bundleCompareAtPrice.value.currencyCode;

      // For BOGO deals, only discount addon products
      if (dealRef.value.isBogo) {
        const baseProductId = dealRef.value.products[0].id;
        const addonProducts = productsWithPrices.filter((p) => p.id !== baseProductId);

        if (addonProducts.length > 0) {
          allocatedPrices = allocateDiscount(
            addonProducts.map((p) => ({ id: p.id, price: { amount: p.priceForAllocation } })),
            totalDiscount,
            currency
          );
        }
      } else {
        // Regular deals: allocate across all products
        allocatedPrices = allocateDiscount(
          productsWithPrices.map((p) => ({ id: p.id, price: { amount: p.priceForAllocation } })),
          totalDiscount,
          currency
        );
      }
    }

    return productsWithPrices.map((product, index) => {
      const result = { ...product };
      const currency = product.selected.price.currencyCode;

      // For BOGO base product, no discount
      if (dealRef.value?.isBogo && product.id === dealRef.value.products[0].id) {
        result.exactDiscountedPrice = product.priceForAllocation;
        result.exactDiscountAmount = 0;
      } else if (allocatedPrices) {
        // Add exact discounted price from allocation
        const allocated = allocatedPrices.find((a) => a.id === product.id);
        if (allocated) {
          result.exactDiscountedPrice = allocated.discountedPrice;
          result.exactDiscountAmount = allocated.discountAmount;
        } else {
          // Fallback: no discount applied
          result.exactDiscountedPrice = product.priceForAllocation;
          result.exactDiscountAmount = 0;
        }
      } else {
        // No discount at all
        result.exactDiscountedPrice = product.priceForAllocation;
        result.exactDiscountAmount = 0;
      }

      // Add display-ready price objects for components
      result.displayPrice = {
        amount: result.exactDiscountedPrice,
        currencyCode: currency,
      };

      // Only show compare price if there's an actual discount
      if (result.exactDiscountAmount > 0) {
        result.displayComparePrice = product.selected.price;
      } else {
        // Otherwise use shopify compareAtPrice if available
        result.displayComparePrice = product.selected.compareAtPrice || null;
      }

      return result;
    });
  });

  /**
   * Calculate bundle compare at price (price without deal discount)
   * Uses currently selected variants
   * Uses Dinero.js for accurate money arithmetic
   */
  const calculateCompareAtPrice = (deal, selectedVars) => {
    if (!deal) return null;

    const products = deal.isBogo ? [...deal.products, ...deal.discountProducts] : deal.products;

    const currency = deal.products[0]?.variants[0]?.price?.currencyCode || 'EUR';

    // Sum prices using Dinero.js
    const total = products.reduce(
      (sum, product) => {
        const variantId = selectedVars[product.id];
        const variant = product.variants.find((v) => v.id === variantId);

        if (!variant) return sum;

        // If deal has a discount, use variant price as compare price
        if (deal.discountValue > 0) {
          return sum.add(money(variant.price.amount, currency));
        }

        // No discount: look for Shopify compareAtPrice
        const shopifyVariant = product.shopifyProduct?.variants?.edges.find((v) => v.node.id.includes(variant.id));

        const priceToUse = shopifyVariant?.node.compareAtPrice
          ? Number(shopifyVariant.node.compareAtPrice.amount)
          : variant.price.amount;

        return sum.add(money(priceToUse, currency));
      },
      money(0, currency)
    );

    return toPriceObject(total);
  };

  /**
   * Calculate bundle price (price WITH deal discount)
   * Uses currently selected variants
   * Uses Dinero.js for accurate money arithmetic
   */
  const calculateBundlePrice = (deal, selectedVars, comparePrice) => {
    if (!deal || !comparePrice) return null;

    // No discount: bundle price = compare price
    if (!deal.discountValue || deal.discountValue === 0) {
      return comparePrice;
    }

    const currency = comparePrice.currencyCode;
    const comparePriceDinero = money(comparePrice.amount, currency);
    const discountValue = money(deal.discountValue, currency);

    // Apply discount based on type
    switch (deal.discountType) {
      case 'fixedAmount': {
        const discountedPrice = comparePriceDinero.subtract(discountValue);
        // Ensure discount doesn't exceed total (min price is 0)
        return toPriceObject(discountedPrice.isPositive() ? discountedPrice : money(0, currency));
      }

      case 'percentage':
        // For BOGO deals, don't discount the base product
        if (deal.isBogo) {
          const baseProductPrice =
            deal.products[0].variants.find((v) => v.id === selectedVars[deal.products[0].id])?.price.amount || 0;

          const basePrice = money(baseProductPrice, currency);
          const amountToDiscount = comparePriceDinero.subtract(basePrice);
          const discountedAmount = amountToDiscount.percentage(100 - deal.discountValue);

          return toPriceObject(basePrice.add(discountedAmount));
        }

        return toPriceObject(comparePriceDinero.percentage(100 - deal.discountValue));

      case 'fixedTargetPrice':
        // For BOGO: base product price + fixed target price
        if (deal.isBogo) {
          const basePrice =
            deal.products[0].variants.find((v) => v.id === selectedVars[deal.products[0].id])?.price.amount || 0;

          return toPriceObject(money(basePrice, currency).add(discountValue));
        }

        return toPriceObject(discountValue);

      default:
        return comparePrice;
    }
  };

  /**
   * Reactive pricing computeds
   */
  const bundleCompareAtPrice = computed(() => {
    return calculateCompareAtPrice(dealRef.value, selectedVariants.value);
  });

  const bundlePrice = computed(() => {
    return calculateBundlePrice(dealRef.value, selectedVariants.value, bundleCompareAtPrice.value);
  });

  /**
   * Update selected variant for a product
   * Triggers reactive recalculation of all prices
   */
  const updateVariant = (productId, variantId) => {
    selectedVariants.value[productId] = parseInt(variantId, 10);
  };

  /**
   * Sync a variant by its Shopify GID (e.g. "gid://shopify/ProductVariant/12345").
   * Finds which product in the deal has that variant and selects it.
   */
  const syncVariantById = (shopifyVariantGid) => {
    if (!dealRef.value || !shopifyVariantGid) return;

    const numericId = parseInt(shopifyVariantGid.split('/').pop(), 10);
    if (isNaN(numericId)) return;

    const products = dealRef.value.isBogo
      ? [...dealRef.value.products, ...dealRef.value.discountProducts]
      : dealRef.value.products;

    const sourceProduct = products.find((p) => p.variants.some((v) => v.id === numericId));
    if (!sourceProduct) return;

    updateVariant(sourceProduct.id, numericId);

    // Cross-product variant matching: apply matching options to peer products
    const sourceOptions = getSelectedOptions(sourceProduct, numericId);
    if (sourceOptions.length) {
      products.forEach((targetProduct) => {
        if (targetProduct.id === sourceProduct.id) return;
        const match = findMatchingVariantByOptions(targetProduct, sourceOptions);
        if (match) updateVariant(targetProduct.id, match.id);
      });
    }
  };

  /**
   * Get variants formatted for PickyStory cart API
   * Returns products and addon products with position
   */
  const getVariantsForCart = () => {
    if (!dealRef.value) return { products: [], addonProducts: null };

    let pos = 0;

    const products = dealRef.value.products.map((product) => {
      const item = productsList.value.find((p) => p.id === product.id);
      pos += 1;
      return {
        id: item?.selected?.id || product.variants[0]?.id,
        position: pos,
      };
    });

    let addonProducts = null;
    if (dealRef.value.isBogo) {
      addonProducts = dealRef.value.discountProducts.map((product) => {
        const item = productsList.value.find((p) => p.id === product.id);
        pos += 1;
        return {
          id: item?.selected?.id || product.variants[0]?.id,
          position: pos,
        };
      });
    }

    return { products, addonProducts };
  };

  return {
    // Reactive state
    productsList,
    selectedVariants,
    bundlePrice,
    bundleCompareAtPrice,

    // Methods
    updateVariant,
    getVariantsForCart,
    syncVariantById,
  };
};
